import { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

interface GalleryImage {
  id: number;
  src: string;
  alt: string;
  category: string;
}

const galleryImages: GalleryImage[] = [
  {
    id: 1,
    src: 'https://i.imgur.com/ZIRHHJc.jpeg',
    alt: 'Paphos Villa Escape',
    category: 'Luxury Pools'
  },
  {
    id: 2,
    src: 'https://i.imgur.com/7Jtcypq.jpeg',
    alt: 'Akamas Serenity',
    category: 'Luxury Pools'
  },
  {
    id: 3,
    src: 'https://i.imgur.com/6ONDltN.jpeg',
    alt: ' Coastal Cascade',
    category: 'Luxury Pools'
  },
  {
    id: 4,
    src: 'https://i.imgur.com/5HQGBLA.jpeg',
    alt: ' Golden Hour Glow',
    category: 'Luxury Pools'
  },
  {
    id: 5,
    src: 'https://i.imgur.com/QHd9mfs.jpeg',
    alt: 'Mediterranean Motif',
    category: 'Luxury Pools'
  },
  {
    id: 6,
    src: 'https://i.imgur.com/0QwMrD5.jpeg',
    alt: 'Aphrodite Villa',
    category: 'Luxury Pools'
  },
  {
    id: 7,
    src: 'https://i.imgur.com/IjlhTP9.jpeg',
    alt: 'Construction',
    category: 'Construction'
  },
  {
    id: 8,
    src: 'https://i.imgur.com/pq7wrcf.jpeg',
    alt: 'Construction',
    category: 'Construction'
  },
  {
    id: 9,
    src: 'https://i.imgur.com/glBJVhk.jpeg',
    alt: 'Construction',
    category: 'Construction'
  },
  {
    id: 10,
    src: 'https://i.imgur.com/p8erJ3N.jpeg',
    alt: 'Construction',
    category: 'Construction'
  },
  {
    id: 11,
    src: 'https://i.imgur.com/mWvQzwk.jpeg',
    alt: 'Pool House',
    category: 'Pool House '
  },
  {
    id: 12,
    src: 'https://i.imgur.com/uNxTxSX.jpeg',
    alt: 'Pool House',
    category: 'Pool House '
  },
  {
    id: 13,
    src: 'https://i.imgur.com/xsHLBhl.jpeg',
    alt: 'Pool House',
    category: 'Pool House '
  }
];

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());

  const categories = ['all', ...new Set(galleryImages.map(img => img.category))];

  const filteredImages = selectedCategory === 'all'
    ? galleryImages
    : galleryImages.filter(img => img.category === selectedCategory);

  // Navigation functions
  const goToPrevious = () => {
    setCurrentImageIndex(prev => 
      prev === 0 ? filteredImages.length - 1 : prev - 1
    );
  };

  const goToNext = () => {
    setCurrentImageIndex(prev => 
      prev === filteredImages.length - 1 ? 0 : prev + 1
    );
  };

  // Update selected image when index changes (only if modal is already open)
  useEffect(() => {
    if (selectedImage && filteredImages[currentImageIndex]) {
      setSelectedImage(filteredImages[currentImageIndex]);
    }
  }, [currentImageIndex, filteredImages, selectedImage]);

  // Handle image click
  const handleImageClick = (image: GalleryImage) => {
    const index = filteredImages.findIndex(img => img.id === image.id);
    setCurrentImageIndex(index);
    setSelectedImage(image);
  };

  // Handle image load
  const handleImageLoad = (imageId: number) => {
    setLoadedImages(prev => new Set(prev).add(imageId));
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!selectedImage) return;
      
      switch (e.key) {
        case 'ArrowLeft':
          goToPrevious();
          break;
        case 'ArrowRight':
          goToNext();
          break;
        case 'Escape':
          setSelectedImage(null);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedImage]);

  return (
    <section id="gallery" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Gallery</h2>
          <p className="text-xl text-gray-600">Explore our portfolio of pool projects and services</p>
        </div>

        {/* Category Filter */}
        <div className="flex justify-center gap-4 mb-12 flex-wrap">
          {categories.map(category => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-6 py-2 rounded-full transition-all duration-300 ${
                selectedCategory === category
                  ? 'bg-cyan-500 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </button>
          ))}
        </div>

        {/* Image Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredImages.map(image => (
            <div
              key={image.id}
              className="group relative overflow-hidden rounded-lg shadow-lg cursor-pointer transform transition-all duration-300 hover:scale-105"
              onClick={() => handleImageClick(image)}
            >
              {/* Loading Placeholder */}
              {!loadedImages.has(image.id) && (
                <div className="w-full h-64 bg-gray-200 animate-pulse flex items-center justify-center">
                  <div className="text-gray-400">Loading...</div>
                </div>
              )}
              
              {/* Actual Image */}
              <img
                src={image.src}
                alt={image.alt}
                loading="lazy"
                onLoad={() => handleImageLoad(image.id)}
                className={`w-full h-64 object-cover transition-transform duration-300 group-hover:scale-110 ${
                  loadedImages.has(image.id) ? 'opacity-100' : 'opacity-0'
                }`}
                style={{
                  display: loadedImages.has(image.id) ? 'block' : 'none'
                }}
              />
              
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-300 flex items-center justify-center">
                <span className="text-white opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all duration-300">
                  View Details
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <div className="relative max-w-4xl w-full">
            {/* Close Button */}
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-12 right-0 text-white hover:text-cyan-500 transition-colors duration-300 z-10"
              aria-label="Close image viewer"
            >
              <X className="h-8 w-8" />
            </button>

            {/* Previous Button */}
            {filteredImages.length > 1 && (
              <button
                onClick={goToPrevious}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-cyan-500 transition-colors duration-300 bg-black bg-opacity-50 rounded-full p-2"
                aria-label="Previous image"
              >
                <ChevronLeft className="h-8 w-8" />
              </button>
            )}

            {/* Next Button */}
            {filteredImages.length > 1 && (
              <button
                onClick={goToNext}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-cyan-500 transition-colors duration-300 bg-black bg-opacity-50 rounded-full p-2"
                aria-label="Next image"
              >
                <ChevronRight className="h-8 w-8" />
              </button>
            )}

            {/* Image */}
            <img
              src={selectedImage.src}
              alt={selectedImage.alt}
              className="w-full h-auto rounded-lg"
            />
            
            {/* Image Info */}
            <div className="mt-4 text-center">
              <h3 className="text-xl font-semibold text-white mb-2">{selectedImage.alt}</h3>
              <p className="text-gray-300">{selectedImage.category}</p>
              {filteredImages.length > 1 && (
                <p className="text-sm text-gray-400 mt-2">
                  {currentImageIndex + 1} of {filteredImages.length}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery; 